// The only place this module talks to the CVCE marketing API (cvce
// `api/routers/marketing*.py`, served at `/cvce/api/marketing/*` and reached
// through `functions/cvce-marketing/[[path]].ts`).
//
// The portal is not a backend (I-4): every score, trend and count is CVCE's.
// This module formats; it does not recompute.

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export type Trend = "up" | "down" | "flat";

/** cvce `schemas/config.py` PerfRowOut. `score` is the 0-100 overall draft score. */
export type SkeletonPerf = {
  skeleton_slug: string;
  score: number;
  delta_pct: number | null;
  trend: Trend | string;
  draft_count: number;
  low_sample: boolean;
};

/** cvce `schemas/config.py` SnippetPerfRowOut. */
export type SnippetPerf = {
  snippet_id: number;
  score: number;
  delta_pct: number | null;
  trend: Trend | string;
  draft_count: number;
  in_skeletons: number;
  low_sample: boolean;
};

/** `available: false` means telemetry could not be read, not that there is none. */
export type PerfData = {
  available: boolean;
  window?: number | null;
  low_sample_threshold?: number | null;
  total_drafts?: number | null;
  skeletons?: SkeletonPerf[] | null;
  snippets?: SnippetPerf[] | null;
};

/** cvce `schemas/config.py` PublishStatusOut, plus the scope marketing_publish adds. */
export type PublishStatus = {
  live_version: number | null;
  live_published_at: string | null;
  unpublished_count: number;
  has_unpublished: boolean;
  publish_scope?: string;
};

// Same-origin via the Pages Function proxy.
const BASE = import.meta.env.VITE_CVCE_MARKETING_API_BASE ?? "/cvce-marketing/v1";

export class CvceMarketingError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`CVCE Marketing API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** A GET whose body is whatever the endpoint sends: enveloped or not. */
export async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new CvceMarketingError(response.status, await safeBody(response));
  }
  return (await response.json()) as T;
}

export const getPerf = () => getJson<Enveloped<PerfData>>("/perf");
export const getPublishStatus = () => getJson<Enveloped<PublishStatus>>("/publish/status");

// ---- Performance (cvce `api/routers/marketing_stats.py`) ----
// Several of these endpoints return a bare body with no S8 envelope; they are
// typed as such rather than pretending a `meta` exists.

/** cvce MarketingAssetOutcomesOut. Rates are fractions (0.412 = 41.2%). */
export type AssetOutcomes = {
  total_assets: number;
  stamped_assets: number;
  measured_broadcasts: number;
  rated: number;
  avg_engine_score: number | null;
  avg_relate: number | null;
  avg_champion_truth: number | null;
  avg_empower: number | null;
  avg_brand_voice: number | null;
  avg_engagement: number | null;
  pass_rate: number | null;
  avg_revisions: number | null;
  open_rate: number | null;
  click_through_rate: number | null;
  conversion_rate?: number | null;
  orders_attributed?: number;
  revenue_per_recipient?: number | null;
  outcome_status: "awaiting_first_stamped_send" | "awaiting_first_metrics" | "measured" | string;
  era_start: string | null;
};

export type VoxShareRow = {
  scope: "weekly" | "overall" | string;
  week_start: string | null;
  sections: number;
  vox_sections: number;
  human_edited_sections: number;
  unedited_share: number | null;
};

export type WeeklyRow = {
  week_start: string;
  assets: number;
  rated: number;
  avg_engine_score: number | null;
  partial: boolean;
};

export type CohortRow = {
  key: string;
  display_name: string;
  assets: number;
  rated: number;
  avg_engine_score: number | null;
  sample_below_min: boolean;
};

export type ImprovementArea = {
  theme_tag: string;
  section_count: number;
  asset_count: number;
  rated_sections: number;
  score_drag: number | null;
  impact: number | null;
};

export type Band = { band: string; low: number; high: number };
export type BandSet = { kpi_id: string; kpi_name: string; unit: string; bands: Band[] };
export type BandsData = Record<string, BandSet>;

export type FormatOption = { content_model: string | null; medium_platform: string | null; asset_count: number };
export type RunOption = { marketing_run_id: string; asset_count: number; latest_asset_at: string | null };

export type Available<K extends string, T> = { available: boolean } & Record<K, T[]>;

export const COHORTS = ["snippet", "skeleton", "medium", "core_need", "operator"] as const;
export type Cohort = (typeof COHORTS)[number];

const COHORT_PATH: Record<Exclude<Cohort, "operator">, string> = {
  snippet: "/stats/by-snippet",
  skeleton: "/stats/by-skeleton",
  medium: "/stats/by-format",
  core_need: "/stats/by-core-need",
};

export const getAssetOutcomes = (q: string) => getJson<AssetOutcomes>(`/stats/asset-outcomes${q}`);
export const getVoxShare = (q: string) => getJson<VoxShareRow[]>(`/stats/vox-share${q}`);
export const getWeekly = (q: string) => getJson<WeeklyRow[]>(`/stats/weekly${q}`);
export const getImprovementAreas = (q: string) => getJson<ImprovementArea[]>(`/stats/improvement-areas${q}`);
export const getCohort = (cohort: Exclude<Cohort, "operator">, q: string) => getJson<CohortRow[]>(`${COHORT_PATH[cohort]}${q}`);
export const getBands = () => getJson<Enveloped<BandsData>>("/stats/bands");
export const getFormatOptions = () => getJson<Enveloped<Available<"formats", FormatOption>>>("/stats/formats");
export const getRunOptions = () => getJson<Enveloped<Available<"runs", RunOption>>>("/stats/runs-options");

export const statsKey = (name: string, q: string) => ["cvce_marketing", "stats", name, q] as const;

// ---- Campaign Log (enveloped) ----

/** cvce MarketingCampaignLogRowOut. */
export type CampaignLogRow = {
  asset_id: string;
  created_at: string;
  marketing_run_id: string | null;
  content_model: string | null;
  skeleton_slug: string;
  eval_verdict: string | null;
  marketing_engine_score: number | null;
  revision_count: number | null;
};

/** cvce MarketingCampaignLogDetailOut. Dimensions are 0-2; sub-scores are the three eval passes. */
export type CampaignLogDetail = CampaignLogRow & {
  score_snippet_integrity: number | null;
  score_communication: number | null;
  score_kb_consistency: number | null;
  relate: number | null;
  champion_truth: number | null;
  empower: number | null;
  brand_voice: number | null;
  engagement: number | null;
  snippet_ids: number[] | null;
  core_need_id: string | null;
  config_version: number;
  asset_rubric_version: string | null;
  readability_grade: number | null;
};

export type CampaignLogData = { available: boolean; campaign_log: CampaignLogRow[] };
export type CampaignLogDetailData = { available: boolean; asset: CampaignLogDetail | null };

export const getCampaignLog = (q: string) => getJson<Enveloped<CampaignLogData>>(`/stats/campaign-log${q}`);
export const getCampaignLogDetail = (assetId: string) =>
  getJson<Enveloped<CampaignLogDetailData>>(`/stats/campaign-log/${encodeURIComponent(assetId)}`);

/** One query key per read, shared by the tabs and the freshness bridge. */
export const perfKey = ["cvce_marketing", "perf"] as const;
export const publishStatusKey = ["cvce_marketing", "publish_status"] as const;

/**
 * One message per API failure. `areaName` comes from the registry via the
 * shell (I-5), never a literal, so a 403 names the right area.
 */
export function messageFor(error: unknown, areaName: string, system = "CVCE"): string {
  if (!(error instanceof CvceMarketingError)) {
    return `We can't reach ${system} right now. Try again shortly.`;
  }
  switch (error.status) {
    case 400:
      return "That filter is not one CVCE reports on.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 503: {
      const code =
        error.body !== null && typeof error.body === "object" ? (error.body as { error?: unknown }).error : null;
      return code === "not_configured"
        ? "The CVCE address is not configured on the portal, so nothing is shown."
        : `We can't reach ${system} right now. Try again shortly.`;
    }
    default:
      return `We can't reach ${system} right now. Try again shortly.`;
  }
}

// ---- Email (cvce `api/routers/marketing_email.py`, EH-9) ----
// Every rate, band, average, total and age below is computed by CVCE from
// BigQuery `ketosource-kpi.drip.*`. Rates are fractions and NULL under 50
// recipients. This module formats them and nothing else.

export type EmailCompleteness = "complete" | "counting" | "incomplete";

/** One Drip send, as `drip.email_scorecard` scores it. */
export type EmailSend = {
  broadcast_id: string;
  broadcast_name: string | null;
  send_date: string;
  send_at: string | null;
  language: string | null;
  audience: string | null;
  is_resend: boolean | null;
  source: string | null;
  recipients: number;
  delivered: number;
  opens_unique: number;
  clicks_unique: number;
  unsubscribes: number;
  bounces: number;
  complaints: number;
  replies: number | null;
  replies_answered: number | null;
  median_reply_latency_hours: number | null;
  open_rate: number | null;
  click_rate: number | null;
  click_to_open_rate: number | null;
  unsubscribe_rate: number | null;
  bounce_rate: number | null;
  complaint_rate: number | null;
  reply_rate: number | null;
  orders_attributed?: number | null;
  revenue_attributed_gbp?: number | null;
  conversion_rate?: number | null;
  revenue_per_recipient?: number | null;
  /** The send's funnel slug from cvce (C10a); "none" = no funnel, absent before C10a is deployed. */
  funnel_slug?: string | null;
  conversion_rate_band?: string | null;
  revenue_per_recipient_band?: string | null;
  open_rate_band: string | null;
  click_rate_band: string | null;
  click_to_open_rate_band: string | null;
  unsubscribe_rate_band: string | null;
  bounce_rate_band: string | null;
  complaint_rate_band: string | null;
  reply_rate_band: string | null;
  /** Content accuracy of the CVCE asset behind this send (null when the send carries no asset stamp). */
  asset_id?: string | null;
  eval_score?: number | null;
  eval_verdict?: string | null;
  eval_passes?: number | null;
  review_findings?: number | null;
  review_verdict?: string | null;
  reviewer?: string | null;
  data_complete: boolean;
  low_sample: boolean;
  completeness: EmailCompleteness | string;
  computed_at: string | null;
  /** Campaign grouping from CVCE: the stamp first, else the standard broadcast name. */
  campaign_key?: string | null;
  campaign_source?: CampaignSource | null;
};

export const PATTERN_BYS = ["language", "audience", "resend"] as const;
export type PatternBy = (typeof PATTERN_BYS)[number];

export type EmailPatternGroup = {
  key: string | null;
  sends: number;
  rated_sends: number;
  avg_open_rate: number | null;
  avg_click_rate: number | null;
  avg_click_to_open_rate: number | null;
  avg_unsubscribe_rate: number | null;
  avg_bounce_rate: number | null;
  avg_reply_rate: number | null;
  too_few_to_compare: boolean;
};

export type ListHealthCounts = {
  snapshot_date: string;
  subscribers: number;
  prospects: number;
  eu_consent_granted: number;
  new_7d: number;
  new_30d: number;
};

export type ListHealthRow = ListHealthCounts & { language: string | null };

export type EmailSendsData = { available: boolean; sends: EmailSend[] };

/** The window's channel rates: weighted over complete sends with 50+ recipients, banded by CVCE. */
export type EmailSummary = Pick<
  EmailSend,
  | "open_rate" | "click_rate" | "click_to_open_rate" | "unsubscribe_rate" | "bounce_rate" | "complaint_rate" | "reply_rate"
  | "orders_attributed" | "revenue_attributed_gbp" | "conversion_rate" | "revenue_per_recipient"
  | "open_rate_band" | "click_rate_band" | "click_to_open_rate_band" | "unsubscribe_rate_band" | "bounce_rate_band"
  | "complaint_rate_band" | "reply_rate_band" | "conversion_rate_band" | "revenue_per_recipient_band"
> & { sends: number; rated_sends: number; recipients: number | null; delivered: number | null };

export type EmailSummaryData = { available: boolean; summary: EmailSummary | null };
export type EmailPatternsData = { available: boolean; by?: PatternBy; groups: EmailPatternGroup[] };
export type ListHealthData = {
  available: boolean;
  latest_snapshot_date?: string | null;
  snapshot_age_days?: number | null;
  days: ListHealthCounts[];
  rows: ListHealthRow[];
};

export type TagNamespace = {
  namespace: string;
  tag_count: number;
  private: boolean;
  tags: { tag: string; active_subscribers: number }[];
};

export type TagTreeData = {
  available: boolean;
  total_tags?: number;
  namespace_count?: number;
  unclassified_count?: number;
  namespaces: TagNamespace[];
};

/** `meta` is absent when CVCE cannot date the data (S8): typed as optional, never faked. */
export type MaybeEnveloped<T> = { data: T; meta?: FreshnessMeta };

export const getEmailSends = (q: string) => getJson<MaybeEnveloped<EmailSendsData>>(`/email/sends${q}`);
export const getEmailPatterns = (q: string, by: PatternBy) => {
  const sep = q ? "&" : "?";
  return getJson<MaybeEnveloped<EmailPatternsData>>(`/email/patterns${q}${sep}by=${by}`);
};
export const getEmailSummary = (q: string) => getJson<MaybeEnveloped<EmailSummaryData>>(`/email/summary${q}`);
export const getListHealth = (q: string) => getJson<MaybeEnveloped<ListHealthData>>(`/email/list-health${q}`);
export const getTagTree = () => getJson<MaybeEnveloped<TagTreeData>>("/email/tags");

export const emailKey = (name: string, q: string) => ["cvce_marketing", "email", name, q] as const;

/** The four split-test rates, click first. Mirrors cvce `SPLIT_METRICS`. */
export const SPLIT_METRICS = ["click_rate", "click_to_open_rate", "open_rate", "unsubscribe_rate"] as const;
export type SplitMetric = (typeof SPLIT_METRICS)[number];

/** One arm of a send: one broadcast, i.e. skeleton x variant x cohort. */
export type SplitArm = {
  broadcast_id: string;
  broadcast_name: string | null;
  send_at: string | null;
  cohort: string | null;
  skeleton_slug: string | null;
  variant: string | null;
  image_slot: string | null;
  recipients: number;
  delivered: number | null;
  opens_unique: number | null;
  clicks_unique: number | null;
  unsubscribes: number | null;
  data_complete: boolean;
  /** true under 100 recipients: values show, bands do not. */
  band_masked: boolean;
} & Record<SplitMetric, number | null> &
  Record<`${SplitMetric}_band`, string | null>;

/** B minus A with its 95% range. `leader` null means no winner: the range crosses zero. */
export type SplitComparison = {
  metric: SplitMetric;
  a_rate: number | null;
  b_rate: number | null;
  diff: number | null;
  ci_low: number | null;
  ci_high: number | null;
  leader: "A" | "B" | null;
};

export type SplitGroup = {
  campaign_key: string;
  language: string | null;
  week: string | null;
  send_at: string | null;
  data_complete: boolean;
  arms: SplitArm[];
  comparison: SplitComparison[] | null;
};

export type SplitTrendRow = {
  week: string;
  skeleton_slug: string | null;
  variant: string | null;
  cohort: string | null;
  sends: number;
  recipients: number;
  delivered: number | null;
} & Record<SplitMetric, number | null>;

export type SplitTestsData = { available: boolean; groups: SplitGroup[]; trend: SplitTrendRow[] };

export const getEmailSplitTests = (q: string) => getJson<MaybeEnveloped<SplitTestsData>>(`/email/split-tests${q}`);

/** How CVCE grouped a send: its campaign stamp, its standard name, or neither ("none"). */
export type CampaignSource = "stamp" | "name" | "none";
export type EmailEsp = "drip" | "ses" | "mixed";
export type EmailSendType = "newsletter" | "broadcast" | "mixed";
export const CAMPAIGN_METRICS = ["open_rate", "click_rate", "unsubscribe_rate", "reply_rate"] as const;
export type CampaignMetric = (typeof CAMPAIGN_METRICS)[number];

/** Totals CVCE summed over a set of sends. Rates are CVCE's; bands are null under 100 recipients. */
export type CampaignTotals = {
  sends: number;
  recipients: number;
  delivered: number;
  opens_unique: number;
  clicks_unique: number;
  unsubscribes: number;
  replies: number;
  band_masked: boolean;
} & Record<CampaignMetric, number | null> &
  Record<`${CampaignMetric}_band`, string | null>;

export type CampaignPart = "first" | "resend" | "all";
export type CampaignCell = CampaignTotals & { language: string | null; part: CampaignPart };

export type EmailCampaign = {
  /** "none" is the bucket of sends that carry no stamp and break the naming rule. */
  campaign_key: string;
  label: string;
  campaign_source: CampaignSource;
  /** Which service sent it; "mixed" when the campaign's sends span both. Absent before CVCE serves it. */
  esp?: EmailEsp | null;
  /** Newsletter (Drip, or an SES manifest with purpose newsletter) or Broadcast (SES one_off). */
  send_type?: EmailSendType | null;
  first_send_at: string | null;
  resend_at: string | null;
  languages: string[];
  sends: number;
  data_complete: boolean;
  first: CampaignTotals;
  resend: CampaignTotals | null;
  all: CampaignTotals;
  non_english_first: CampaignTotals | null;
  english_share: number | null;
  opened_in_total_rate: number | null;
  cells: CampaignCell[];
  /** SES sends counted by the send job's own `sent` events; null for Drip. Absent before CVCE serves it. */
  sent?: number | null;
  /** An SES send was still going when CVCE last counted (its last `sent` event under a minute old). */
  sending?: boolean | null;
  /** How many sends sit inside the campaign (each SES wave; a Drip email's first send and resend). */
  send_count?: number;
  last_send_at?: string | null;
  sends_detail?: CampaignSendDetail[];
};

/** One send inside a campaign: its own name, time, counts, rates and language breakdown. */
export type CampaignSendDetail = {
  send_key: string;
  label: string;
  send_at: string | null;
  esp?: EmailEsp | null;
  send_type?: EmailSendType | null;
  sent: number | null;
  sending: boolean | null;
  data_complete: boolean | null;
  totals: CampaignTotals;
  cells: CampaignCell[];
};

export type EmailCampaignsData = { available: boolean; campaigns: EmailCampaign[]; by_language: CampaignCell[] };

export const getEmailCampaigns = (q: string) => getJson<MaybeEnveloped<EmailCampaignsData>>(`/email/campaigns${q}`);

// ---- Chart Styles (cvce `api/routers/marketing_chart_templates.py`) ----
// The chart-template registry. cvce holds every row and decides every state;
// this module lists them and calls approve/reject, nothing more.

export type ChartTemplateStatus = "draft" | "live" | "retired";

export type ChartTemplateUse = {
  asset_id: string;
  variant: string;
  revision_id: string;
  used_at: string;
};

/** The D1 template object, stored verbatim by cvce. Only the fields the tab reads are typed. */
export type ChartTemplateBody = {
  schema?: string;
  slug?: string;
  display_name?: string;
  description?: string;
  primitive?: string;
  standard_claim_kind?: string;
  chart_type?: string;
  input?: Record<string, unknown>;
  brief?: { required?: string[]; optional?: string[] };
  layout?: Record<string, unknown>;
  example?: { asset_id?: string; variant?: string; brief_file?: string };
  [key: string]: unknown;
};

/** One item of `GET /chart-templates`: a slug's latest version. */
export type ChartTemplateItem = {
  slug: string;
  display_name: string;
  /** Browse-card title (presentation only, GATE-981); null falls back to display_name. */
  card_title?: string | null;
  primitive: string;
  standard_claim_kind: string;
  template_status: ChartTemplateStatus | string;
  version: number;
  live_version: number | null;
  thumbnail_url: string | null;
  drafted_by: string;
  drafted_at: string;
  approved_by: string | null;
  approved_at: string | null;
  template: ChartTemplateBody;
  example_asset_id: string | null;
  example_verdict: string | null;
  uses: ChartTemplateUse[];
};

/** One row of `GET /chart-templates/{slug}` `versions`. */
export type ChartTemplateVersion = Omit<ChartTemplateItem, "live_version"> & {
  example_brief: Record<string, unknown> | null;
  draft_of_version: number | null;
  rejected_by: string | null;
  rejected_at: string | null;
  reject_note: string | null;
  approval_source: string | null;
};

export type ChartTemplateDetail = {
  slug: string;
  live_version: number | null;
  versions: ChartTemplateVersion[];
};

/** cvce `GET /cvce/api/me`, reached through the `functions/cvce` proxy. */
export type CvceMe = { principal: string | null; may_approve: boolean };

export const getChartTemplates = () =>
  getJson<Enveloped<{ templates: ChartTemplateItem[] }>>("/chart-templates");
export const getChartTemplate = (slug: string) =>
  getJson<Enveloped<ChartTemplateDetail>>(`/chart-templates/${encodeURIComponent(slug)}`);

export async function getCvceMe(): Promise<CvceMe> {
  const response = await fetch("/cvce/api/me", {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new CvceMarketingError(response.status, await safeBody(response));
  return (await response.json()) as CvceMe;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new CvceMarketingError(response.status, await safeBody(response));
  return (await response.json()) as T;
}

const versionPath = (slug: string, version: number) =>
  `/chart-templates/${encodeURIComponent(slug)}/versions/${version}`;

export const approveChartTemplate = (slug: string, version: number) =>
  postJson<Enveloped<{ slug: string; version: number; template_status: string }>>(
    `${versionPath(slug, version)}/approve`,
    {},
  );
export const rejectChartTemplate = (slug: string, version: number, note: string | null) =>
  postJson<Enveloped<{ slug: string; version: number; template_status: string }>>(
    `${versionPath(slug, version)}/reject`,
    { note },
  );

export const chartTemplatesKey = ["cvce_marketing", "chart_templates"] as const;
export const chartTemplateKey = (slug: string) => ["cvce_marketing", "chart_templates", slug] as const;
export const cvceMeKey = ["cvce", "me"] as const;

/** The message shown when approve or reject fails. Nothing changed in every case. */
export function decisionMessage(error: unknown): string {
  if (error instanceof CvceMarketingError) {
    if (error.status === 403) return "Only an approver can sign this off.";
    if (error.status === 409) return "This version changed since you opened the page. Refreshed.";
  }
  return "Couldn't save that. Nothing changed. Try again.";
}
